import { ReactNode, useEffect, useRef, useState } from "react";
import { Box } from "@chakra-ui/react";
import { colors, ColorIcon, customEvent, SafeGTMProperties } from "goi_common";

import { BaseButton } from "../../components";
import { BaseStack } from "../../layout";
import { baseColors, baseShadow, baseSpacing } from "../../tokens";

/**
 * 어느 버튼으로 걸었는지.
 * - banner: 첫 화면 배너의 "지금 바로 전화하기"
 * - floating: 배너가 스크롤로 사라진 뒤 하단에 뜨는 버튼
 */
export type UrgentCallSource = "banner" | "floating";

/** 버튼별 페이지 이벤트 trigger */
const URGENT_CALL_TRIGGERS = {
  banner: "urgent_bnr_call",
  floating: "urgent_flt_call",
} as const;

interface UrgentCallBannerProps {
  /** 장례식장 이름. 있으면 "임종 접수 및 {이름} 장례 상담" 으로 쓴다 */
  placeName?: string;
  /** 제목 아래 두 줄을 바꿔 쓴다. 진행 중인 실험(www #358·#359, parlor #360)은 화면이 바뀌면 안 돼 넘기지 않는다 */
  description?: ReactNode;
  /** 페이지별 클릭 이벤트 path. 공통 이벤트(urgent_call_click)의 properties.page 로도 들어간다 */
  eventPath:
    | "parlor_match_request"
    | "match_request"
    | "parlor_ad_quote"
    | "prlr_fam_main"
    | "prlr_mubinso";
  eventProperties?: SafeGTMProperties;
  /** 하단 고정 바가 이미 있는 페이지는 하단 버튼을 띄우지 않는다. 두 개가 겹친다 */
  hideFloating?: boolean;
  /**
   * 있으면 배너 버튼에 번호를 같이 보인다. 데스크톱은 눌러서 걸 수 없어 번호를 보고 직접 입력한다.
   * 진행 중인 실험(www #358·#359, parlor #360)은 화면이 바뀌면 안 돼 넘기지 않는다
   */
  phoneNumber?: string;
  /** 배너가 화면 위로 사라졌는지 알린다. 하단 버튼 대신 페이지 자체 전화 띠지를 띄울 때 쓴다 */
  onScrolledPastChange?: (isScrolledPast: boolean) => void;
  /** 이벤트는 이 컴포넌트가 쏜다. 호출부는 전화/모달 동작만 한다 */
  onCall: (source: UrgentCallSource) => void;
}

/**
 * 첫 화면 임종 배너. 임종 고객이 폼까지 내려가기 전에 바로 전화로 보낸다.
 * 스크롤로 배너가 화면 위로 사라지면 하단에 작은 전화 버튼을 띄운다. 첫 화면에서는 배너만 보인다.
 *
 * "고이장례연구소가 장례 전 과정을 도와드려요" 는 지우지 않는다(description 으로 바꿀 때도 "고이에서" 처럼 주체를 남긴다). 이 배너가 대신하는 "잠깐! 이곳은 고이장례연구소입니다"
 * 띠지가 광고 법적 리스크(장례식장 공식 페이지 오인) 때문에 있던 것이라, 운영 주체를 밝히는 역할을 여기서 한다.
 * "공식 연락처 아님" 같은 부정형 고지는 쓰지 않는다(삼고처럼 주체만 긍정형으로 밝힌다, 2026-10-13 영민).
 *
 * 글꼴 크기를 클래스가 아니라 props 로 준다. parlor 는 design-system typography.css 를
 * 불러오지 않아 클래스를 쓰면 그쪽에서 스타일이 빠진다.
 */
export const UrgentCallBanner = ({
  placeName,
  description,
  eventPath,
  eventProperties,
  hideFloating = false,
  onScrolledPastChange,
  phoneNumber,
  onCall,
}: UrgentCallBannerProps) => {
  const bannerRef = useRef<HTMLDivElement>(null);

  // 페이지별 이벤트(click-{path}-urgent_*_call)와 세 페이지 공통 이벤트(urgent_call_click)를 같이 쏜다.
  // 공통 이벤트는 Hackle 지표로 실험끼리 같은 이름을 쓰려고 둔다.
  const handleCall = (source: UrgentCallSource) => {
    customEvent({
      newGtm: {
        path: eventPath,
        trigger_and_target: URGENT_CALL_TRIGGERS[source],
        properties: eventProperties,
      },
    });
    customEvent({
      newGtm: {
        action: "urgent_call_click",
        // 장례식장 id 같은 페이지 속성은 위 페이지 이벤트에 있다. 공통 이벤트는 페이지와 버튼만 싣는다
        properties: { page: eventPath, source },
      },
    });
    onCall(source);
  };
  const [isScrolledPast, setIsScrolledPast] = useState(false);

  useEffect(() => {
    const el = bannerRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(([entry]) => {
      // 배너가 화면 "위로" 나갔을 때만. 아래에 있어 아직 안 보이는 경우는 제외한다
      // 경계에 딱 붙은 경우(배너 아래 끝 = 화면 맨 위)는 isIntersecting 이 true 로 남아서 보이는 면적(ratio)으로 본다
      setIsScrolledPast(
        entry.intersectionRatio === 0 && entry.boundingClientRect.top < 0
      );
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    onScrolledPastChange?.(isScrolledPast);
  }, [isScrolledPast, onScrolledPastChange]);

  return (
    <>
      {/* A안 띠지(CompanyInfoBanner)처럼 화면 끝까지 꽉 찬 띠. 바탕은 연하게 두고 진한 색은 전화 버튼에만 써서
          눈이 버튼으로 가게 한다(꽉 찬 진주황 바탕은 눈부셔서 오히려 버튼이 묻혔다, 2026-10-13 영민) */}
      <div ref={bannerRef}>
        <BaseStack
          width="100%"
          alignItems="center"
          textAlign="center"
          bg={baseColors.orange_50}
          borderBottom={`1px solid ${baseColors.orange_100}`}
          padding={`${baseSpacing.sp_32}px ${baseSpacing.sp_16}px ${baseSpacing.sp_24}px`}
          spacing={baseSpacing.sp_4}
          wordBreak="keep-all"
        >
          <Box
            fontSize="18px"
            fontWeight={700}
            lineHeight="26px"
            color={colors.orange650}
          >
            임종이 발생하셨나요?
          </Box>
          <Box
            fontSize="15px"
            fontWeight={400}
            lineHeight="22px"
            color={baseColors.label_normal}
          >
            {description ?? (
              <>
                {placeName
                  ? `임종 접수 및 ${placeName} 장례 상담`
                  : "임종 접수 및 장례 상담"}
                <br />
                고이장례연구소가 장례 전 과정을 도와드려요
              </>
            )}
          </Box>
          <Box paddingTop={`${baseSpacing.sp_8}px`}>
            <BaseButton
              type="primary"
              size="medium"
              rounded
              onClick={() => handleCall("banner")}
            >
              <CallLabel color={colors.white}>
                {phoneNumber
                  ? `${phoneNumber} 지금 전화하기`
                  : "지금 바로 전화하기"}
              </CallLabel>
            </BaseButton>
          </Box>
        </BaseStack>
      </div>
      {isScrolledPast && !hideFloating && (
        <Box
          position="fixed"
          left="50%"
          bottom="calc(16px + env(safe-area-inset-bottom))"
          transform="translateX(-50%)"
          zIndex={1000}
          boxShadow={baseShadow.accent}
          borderRadius="999px"
        >
          <BaseButton
            type="primary"
            size="medium"
            rounded
            onClick={() => handleCall("floating")}
          >
            <CallLabel color={colors.white}>임종 접수 · 24시간 전화</CallLabel>
          </BaseButton>
        </Box>
      )}
    </>
  );
};

const CallLabel = ({
  color,
  children,
}: {
  color: string;
  children: string;
}) => (
  <BaseStack
    direction="horizontal"
    spacing={baseSpacing.sp_4}
    alignItems="center"
    px={baseSpacing.sp_8}
  >
    <ColorIcon name="icon-call-mono" color={color} width={16} height={16} />
    <Box fontSize="15px" fontWeight={600} whiteSpace="nowrap" color={color}>
      {children}
    </Box>
  </BaseStack>
);
